<template>
  <v-card
    v-click-outside="handleFocusOut"
    flat
    tabindex="-1"
  >
    <v-container class="pa-3 spec-container">
      <v-row
        v-if="selected.length > 0"
        class="mb-3"
      >
        <v-chip
          v-for="(item, index) in selected"
          :key="`${item.id}-${index}`"
          color="primary"
          closable
          class="spec-chip mt-0 mr-1 mb-1"
          :data-testid="`chip-${item.title}`"
          @click:close="handleRemove(item.id)"
        >
          {{ item.title }}
        </v-chip>
      </v-row>
      <v-row>
        <div class="search">
          <!-- Using v-autocomplete with the multiple prop as a workaround for keeping the search results open after
          selection. -->
          <v-autocomplete
            ref="autocompleteRef"
            v-model="autocompleteModel"
            autofocus
            :items="available"
            :placeholder="placeholder"
            :item-title="item => item.title"
            :item-value="item => item"
            hide-details
            hide-selected
            density="compact"
            :loading="loading"
            :no-data-text="noDataText"
            variant="outlined"
            menu-icon=""
            append-inner-icon="mdi-magnify"
            multiple
            :menu-props="{ ref: 'menuRef' }"
            class="search__autocomplete"
            @update:model-value="handleSelect"
          >
            <template #item="{ item, props }">
              <v-list-item
                v-bind="props"
                class="search__result"
              >
                <template #prepend>
                  <v-avatar
                    v-if="iconUrlFor(item.raw)"
                    size="20"
                  >
                    <img
                      :src="iconUrlFor(item.raw)"
                      class="search__result-icon"
                    >
                  </v-avatar>
                </template>
                <template #title>
                  <span class="search__result-text">{{ item.raw.title }}</span>
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>
        </div>
      </v-row>
    </v-container>
    <FilterSpecButtons
      @close-filter-specification="$emit('close-filter-specification')"
      @apply-filters="applyFilters"
    />
  </v-card>
</template>

<script lang="ts">
import type {PropType} from 'vue';

import FilterSpecButtons from './FilterSpecButtons.vue';
import type {FilterItem} from '@/lib/filters/types';

export default {
  name: 'FilterSpecAutocomplete',
  components: {
    FilterSpecButtons,
  },
  props: {
    /** Carries `items` (every value on offer) and `selected` (the values already chosen). */
    filter: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    noDataText: {
      type: String,
      default: undefined,
    },
    iconUrl: {
      type: String,
      default: '',
    },
    getItemIconUrl: {
      type: Function as PropType<(item: FilterItem) => string | undefined>,
      default: undefined,
    },
  },
  emits: ['close-filter-specification', 'update-filters'],
  data() {
    return {
      selected: [...(this.filter.selected ?? [])] as FilterItem[],
      autocompleteModel: [] as FilterItem[],
    };
  },
  computed: {
    /** Already-chosen values are chips rather than list entries, so they drop out of the menu. */
    available(): FilterItem[] {
      return (this.filter.items ?? []).filter(
        (item: FilterItem) => !this.selected.some((one) => one.id === item.id),
      );
    },
    placeholder(): string {
      return this.filter.placeholderText || 'Search...';
    },
  },
  methods: {
    iconUrlFor(item: FilterItem): string {
      return this.getItemIconUrl?.(item) || this.iconUrl;
    },
    async handleSelect(picked: FilterItem[]) {
      const added = picked.filter((item) => item.id && !this.selected.some((one) => one.id === item.id));
      this.selected = [...this.selected, ...added];
      this.autocompleteModel = [];

      // Each new chip shifts the input down, and the menu does not follow on its own.
      await this.$nextTick();
      this.$refs.autocompleteRef?.$refs.menuRef?.updateLocation?.();
    },
    handleRemove(id: string) {
      this.selected = this.selected.filter((item) => item.id !== id);
    },
    applyFilters() {
      this.$emit('update-filters', {...this.filter, selected: this.selected});
      this.$emit('close-filter-specification');
    },
    handleFocusOut() {
      // Don't do anything if a child element is focused
      if (this.$el.matches(':focus-within')) return;
      this.$emit('close-filter-specification');
    },
  },
};
</script>

<style scoped lang="scss">
.spec-container {
  width: 350px;
  max-width: 350px;
}

.spec-chip {
  max-width: 100%;
  font-weight: bold;
  font-size: 13px;
  font-family: main.$fontPrimary;
  :deep(.v-chip__underlay) {
    opacity: 0.2;
    background-color: #ffa600;
  }
  :deep(.v-chip__content) {
    overflow: hidden;
  }
}

// Below styles here are based on the styles in AlgoliaAutocomplete.vue
:deep(.v-list-item-title) {
  @extend .display-field-text;
}

:deep(.v-list-item__prepend) {
  margin-right: -8px;
}

.search {
  width: 100%;
  @extend .display-field-text;

  &__result {
    height: 28px;
    min-height: 28px;
    padding-left: 8px !important;
    --v-border-color: #ebebeb;
    --v-border-opacity: 0;
    &:hover {
      background-color: rgba(235, 235, 235, 1);
    }
    &:focus-visible{
      background-color: rgba(235, 235, 235, 0.55);
      &::after {
        opacity: 0;
      }
    }
    &-icon {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    &-text {
      @extend .display-field-text;
    }
  }

  &__placeholder {
    @extend .display-field-text;
  }

  /* Make autocomplete compact and match display-field-text styling */
  :deep(.v-field) {
    min-height: 28px !important;
  }

  :deep(.v-field__input) {
    min-height: 28px !important;
    padding: 4px 4px 4px 12px;
  }

  :deep(.v-field__input input) {
    @extend .display-field-text;
  }

  :deep(.v-field__clearable i) {
    font-size: 22px;
  }
  :deep(.v-field__append-inner i) {
    font-size: 22px;
  }
}
</style>
